"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { API_URL } from "../config"
import "./DeviceData.css"
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps"

const GOOGLE_MAPS_API_KEY = "AIzaSyCT_w1O7dUdXlSvWCRn-DxU8e11Vd0t94k"

const DeviceData = () => {
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAddDevice, setShowAddDevice] = useState(false)
  const [editingDevice, setEditingDevice] = useState(null)

  // Device form state
  const [deviceForm, setDeviceForm] = useState({
    name: "",
    date: new Date().toISOString().split("T")[0],
    code: "",
    selectedParameters: [],
    location: {
      coordinates: [0, 0],
      address: "",
    },
    height: "2", // Default height set to 2
  })

  // Updated parameters list as requested
  const availableParameters = [
    { id: "tmax", name: "Maximum Temperature", unit: "°C", description: "Daily maximum temperature" },
    { id: "ta", name: "Average Temperature", unit: "°C", description: "Daily average temperature" },
    { id: "tmin", name: "Minimum Temperature", unit: "°C", description: "Daily minimum temperature" },
    { id: "stationFetch", name: "Station Fetch", unit: "km", description: "Distance to weather station" },
    { id: "rhmax", name: "Maximum Relative Humidity", unit: "%", description: "Daily maximum relative humidity" },
    { id: "rha", name: "Average Relative Humidity", unit: "%", description: "Daily average relative humidity" },
    { id: "rhmin", name: "Minimum Relative Humidity", unit: "%", description: "Daily minimum relative humidity" },
    { id: "uz", name: "Wind Speed at Height Z", unit: "m/s", description: "Wind speed at measurement height" },
    { id: "ud", name: "Wind Speed During Day", unit: "m/s", description: "Daytime wind speed" },
    { id: "un", name: "Wind Speed During Night", unit: "m/s", description: "Nighttime wind speed" },
    { id: "zw", name: "Wind Measurement Height", unit: "m", description: "Height of wind measurement" },
    { id: "zp", name: "Psychrometer Height", unit: "m", description: "Height of psychrometer" },
    { id: "n", name: "Sunshine Hours", unit: "hours", description: "Daily sunshine duration" },
    { id: "rs", name: "Solar Radiation", unit: "MJ/m²/day", description: "Daily solar radiation" },
    { id: "rn", name: "Net Radiation", unit: "MJ/m²/day", description: "Daily net radiation" },
    { id: "epan", name: "Pan Evaporation", unit: "mm/day", description: "Daily pan evaporation" },
  ]

  // Map state
  const [showMap, setShowMap] = useState(false)
  const [mapCenter, setMapCenter] = useState([0, 0])

  // Auto-generate device name
  const generateDeviceName = () => {
    const timestamp = Date.now().toString().slice(-4)
    const deviceName = `Device-${timestamp}`
    setDeviceForm((prev) => ({
      ...prev,
      name: deviceName,
    }))
  }

  // Set current location as default for map and form
  useEffect(() => {
    if (deviceForm.location.coordinates[0] === 0 && deviceForm.location.coordinates[1] === 0 && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords
          setMapCenter([longitude, latitude])
          setDeviceForm((prev) => ({
            ...prev,
            location: {
              ...prev.location,
              coordinates: [longitude, latitude],
            },
          }))
        },
        () => {
          // fallback: leave as [0,0]
        },
      )
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    fetchDevices()
  }, [])

  // Auto-generate device name when component mounts or when adding new device
  useEffect(() => {
    if (showAddDevice && !editingDevice && !deviceForm.name) {
      generateDeviceName()
    }
  }, [showAddDevice, editingDevice])

  const fetchDevices = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      const config = {
        headers: {
          "x-auth-token": token,
        },
      }

      const devicesRes = await axios.get(`${API_URL}/api/devices`, config)
      setDevices(devicesRes.data)
      setLoading(false)
    } catch (err) {
      setError("Failed to load devices")
      setLoading(false)
      console.error(err)
    }
  }

  const handleFormChange = (e) => {
    const { name, value } = e.target

    // Handle height validation
    if (name === "height") {
      const heightValue = Number.parseFloat(value)
      if (value === "" || (heightValue >= 1 && heightValue <= 10)) {
        setDeviceForm((prev) => ({
          ...prev,
          [name]: value,
        }))
      }
      return
    }

    setDeviceForm((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleParameterToggle = (parameterId) => {
    setDeviceForm((prev) => ({
      ...prev,
      selectedParameters: prev.selectedParameters.includes(parameterId)
        ? prev.selectedParameters.filter((id) => id !== parameterId)
        : [...prev.selectedParameters, parameterId],
    }))
  }

  const handleLocationChange = (coordinates, address = "") => {
    setDeviceForm((prev) => ({
      ...prev,
      location: {
        coordinates,
        address,
      },
    }))
  }

  const handleMapSelect = () => {
    setShowMap(true)
    setMapCenter(deviceForm.location.coordinates)
  }

  const handleMapConfirm = (coordinates) => {
    handleLocationChange(coordinates)
    setShowMap(false)
  }

  const generateDeviceCode = () => {
    const timestamp = Date.now().toString().slice(-6)
    const namePrefix = deviceForm.name.slice(0, 3).toUpperCase() || "DEV"
    const code = `${namePrefix}-${timestamp}`
    setDeviceForm((prev) => ({
      ...prev,
      code,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate height
    const heightValue = Number.parseFloat(deviceForm.height)
    if (!deviceForm.height || heightValue < 1 || heightValue > 10) {
      setError("Device height must be between 1 and 10 meters")
      return
    }

    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      const config = {
        headers: {
          "x-auth-token": token,
        },
      }

      const deviceData = {
        name: deviceForm.name,
        date: deviceForm.date,
        code: deviceForm.code,
        selectedParameters: deviceForm.selectedParameters,
        coordinates: deviceForm.location.coordinates,
        address: deviceForm.location.address,
        height: Number.parseFloat(deviceForm.height),
      }

      if (editingDevice) {
        // Update device
        const res = await axios.put(`${API_URL}/api/devices/${editingDevice._id}`, deviceData, config)
        setDevices((prev) => prev.map((device) => (device._id === editingDevice._id ? res.data : device)))
      } else {
        // Create new device
        const res = await axios.post(`${API_URL}/api/devices`, deviceData, config)
        setDevices((prev) => [...prev, res.data])
      }

      // Reset form
      resetForm()
      setLoading(false)
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to save device")
      setLoading(false)
      console.error(err)
    }
  }

  const resetForm = () => {
    setDeviceForm({
      name: "",
      date: new Date().toISOString().split("T")[0],
      code: "",
      selectedParameters: [],
      location: {
        coordinates: [0, 0],
        address: "",
      },
      height: "2", // Reset to default height
    })
    setShowAddDevice(false)
    setEditingDevice(null)
  }

  const handleEdit = (device) => {
    setEditingDevice(device)
    setDeviceForm({
      name: device.name,
      date: device.date ? device.date.split("T")[0] : new Date().toISOString().split("T")[0],
      code: device.code,
      selectedParameters: device.selectedParameters || [],
      location: {
        coordinates: device.location?.coordinates || [0, 0],
        address: device.address || "",
      },
      height: device.height?.toString() || "2",
    })
    setShowAddDevice(true)
  }

  const handleDelete = async (deviceId) => {
    if (!window.confirm("Are you sure you want to delete this device?")) {
      return
    }

    try {
      const token = localStorage.getItem("token")
      const config = {
        headers: {
          "x-auth-token": token,
        },
      }

      await axios.delete(`${API_URL}/api/devices/${deviceId}`, config)
      setDevices((prev) => prev.filter((device) => device._id !== deviceId))
    } catch (err) {
      setError("Failed to delete device")
      console.error(err)
    }
  }

  const getParameterName = (parameterId) => {
    const param = availableParameters.find((p) => p.id === parameterId)
    return param ? param.name : parameterId
  }

  const getParameterUnit = (parameterId) => {
    const param = availableParameters.find((p) => p.id === parameterId)
    return param ? param.unit : ""
  }

  const selectAllParameters = () => {
    setDeviceForm((prev) => ({
      ...prev,
      selectedParameters: availableParameters.map((param) => param.id),
    }))
  }

  const clearAllParameters = () => {
    setDeviceForm((prev) => ({
      ...prev,
      selectedParameters: [],
    }))
  }

  const handleAddNewDevice = () => {
    setShowAddDevice(true)
    // Auto-generate name for new device
    if (!editingDevice) {
      generateDeviceName()
    }
  }

  return (
    <div className="device-data-container">
      <div className="device-data-header">
        <h1>Weather Parameter Device Management</h1>
        <p className="device-data-subtitle">Manage your weather monitoring devices</p>
        <button className="add-device-button" onClick={handleAddNewDevice}>
          <i className="fas fa-plus"></i>
          Add New WPD
        </button>
      </div>

      {error && (
        <div className="alert-error device-alert">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button onClick={() => setError(null)} className="alert-close">
            ×
          </button>
        </div>
      )}

      {/* Add/Edit Device Form */}
      {showAddDevice && (
        <div className="device-form-modal">
          <div className="device-form-container">
            <div className="form-header">
              <h2>{editingDevice ? "Edit Device" : "Add New Device"}</h2>
              <button className="close-button" onClick={resetForm}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="device-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Device Name *</label>
                  <div className="name-input-group">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={deviceForm.name}
                      onChange={handleFormChange}
                      className="form-control"
                      required
                      placeholder="Enter device name"
                    />
                    <button type="button" className="generate-name-button" onClick={generateDeviceName}>
                      <i className="fas fa-magic"></i>
                      Auto-Generate
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="date">Date *</label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={deviceForm.date}
                    onChange={handleFormChange}
                    className="form-control"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="code">Device Code *</label>
                  <div className="code-input-group">
                    <input
                      type="text"
                      id="code"
                      name="code"
                      value={deviceForm.code}
                      onChange={handleFormChange}
                      className="form-control"
                      required
                      placeholder="Enter or generate device code"
                    />
                    <button type="button" className="generate-code-button" onClick={generateDeviceCode}>
                      <i className="fas fa-magic"></i>
                      Generate
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="height">Device Height (meters) *</label>
                  <input
                    type="number"
                    id="height"
                    name="height"
                    value={deviceForm.height}
                    onChange={handleFormChange}
                    className="form-control"
                    step="0.1"
                    min="1"
                    max="10"
                    required
                    placeholder="Height between 1-10 meters"
                  />
                  <small className="form-text text-muted">Height must be between 1 and 10 meters (Default: 2m)</small>
                </div>
              </div>

              <div className="form-group">
                <label>Location</label>
                <div className="location-input-group">
                  <div className="coordinates-display">
                    <div className="coordinate-item">
                      <span className="coordinate-label">Latitude:</span>
                      <span className="coordinate-value">{deviceForm.location.coordinates[1].toFixed(6)}</span>
                    </div>
                    <div className="coordinate-item">
                      <span className="coordinate-label">Longitude:</span>
                      <span className="coordinate-value">{deviceForm.location.coordinates[0].toFixed(6)}</span>
                    </div>
                  </div>
                  <button type="button" className="map-select-button" onClick={handleMapSelect}>
                    <i className="fas fa-map-marker-alt"></i>
                    Select from Map
                  </button>
                </div>
                {deviceForm.location.address && (
                  <div className="address-display">
                    <i className="fas fa-map-pin"></i>
                    {deviceForm.location.address}
                  </div>
                )}
              </div>

              <div className="form-group">
                <div className="parameters-header">
                  <label>Weather Parameters</label>
                  <div className="parameter-actions">
                    <button type="button" className="select-all-button" onClick={selectAllParameters}>
                      Select All
                    </button>
                    <button type="button" className="clear-all-button" onClick={clearAllParameters}>
                      Clear All
                    </button>
                    <span className="selected-count">
                      {deviceForm.selectedParameters.length} of {availableParameters.length} selected
                    </span>
                  </div>
                </div>
                <div className="parameters-grid">
                  {availableParameters.map((param) => (
                    <div key={param.id} className="parameter-item">
                      <label className="parameter-checkbox">
                        <input
                          type="checkbox"
                          checked={deviceForm.selectedParameters.includes(param.id)}
                          onChange={() => handleParameterToggle(param.id)}
                        />
                        <div className="parameter-info">
                          <div className="parameter-header">
                            <span className="parameter-name">{param.name}</span>
                            <span className="parameter-unit">({param.unit})</span>
                          </div>
                          <span className="parameter-description">{param.description}</span>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      {editingDevice ? "Updating..." : "Adding..."}
                    </>
                  ) : (
                    <>
                      <i className={`fas ${editingDevice ? "fa-save" : "fa-plus"}`}></i>
                      {editingDevice ? "Update Device" : "Add Device"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Map Modal */}
      {showMap && (
        <div className="map-modal">
          <div className="map-container">
            <div className="map-header">
              <h3>Select Device Location</h3>
              <button className="close-button" onClick={() => setShowMap(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="map-content">
              <div style={{ width: "100%", height: "100%" }}>
                <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                  <Map
                    center={{ lat: mapCenter[1], lng: mapCenter[0] }}
                    zoom={mapCenter[0] === 0 && mapCenter[1] === 0 ? 2 : 12}
                    style={{ width: "100%", height: "100%" }}
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    onClick={(e) => {
                      const { lat, lng } = e.detail.latLng
                      setMapCenter([lng, lat])
                    }}
                    onCenterChanged={(e) => {
                      const center = e.detail.center
                      setMapCenter([center.lng, center.lat])
                    }}
                  >
                    <Marker position={{ lat: mapCenter[1], lng: mapCenter[0] }} />
                  </Map>
                </APIProvider>
              </div>
              <div className="current-coordinates">
                <p>Current Coordinates:</p>
                <p>
                  Latitude: {mapCenter[1].toFixed(6)}, Longitude: {mapCenter[0].toFixed(6)}
                </p>
              </div>
              <div className="map-actions">
                <button className="confirm-location-button" onClick={() => handleMapConfirm(mapCenter)}>
                  <i className="fas fa-check"></i>
                  Confirm Location
                </button>
                <button className="cancel-location-button" onClick={() => setShowMap(false)}>
                  <i className="fas fa-times"></i>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Devices List */}
      <div className="devices-card">
        <div className="card-header">
          <h2>Weather Parameter Devices ({devices.length})</h2>
          <div className="devices-stats">
            <span className="stat-item">
              <i className="fas fa-microchip"></i>
              Total: {devices.length}
            </span>
            <span className="stat-item">
              <i className="fas fa-check-circle"></i>
              Active: {devices.filter((d) => d.active).length}
            </span>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading devices...</p>
            </div>
          ) : devices.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-microchip empty-icon"></i>
              <h3>No devices found</h3>
              <p>Start by adding your first weather monitoring device</p>
              <button className="add-first-device-button" onClick={handleAddNewDevice}>
                <i className="fas fa-plus"></i>
                Add Your First Device
              </button>
            </div>
          ) : (
            <div className="devices-grid">
              {devices.map((device) => (
                <div key={device._id} className="device-card">
                  <div className="device-header">
                    <div className="device-icon">
                      <i className="fas fa-microchip"></i>
                    </div>
                    <div className="device-title">
                      <h3>{device.name}</h3>
                      <p className="device-code">{device.code}</p>
                    </div>
                    <div className="device-actions">
                      <button className="edit-button" onClick={() => handleEdit(device)} title="Edit Device">
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="delete-button" onClick={() => handleDelete(device._id)} title="Delete Device">
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>

                  <div className="device-details">
                    <div className="detail-item">
                      <span className="detail-label">
                        <i className="fas fa-calendar"></i>
                        Date:
                      </span>
                      <span className="detail-value">
                        {device.date ? new Date(device.date).toLocaleDateString() : "Not set"}
                      </span>
                    </div>

                    <div className="detail-item">
                      <span className="detail-label">
                        <i className="fas fa-ruler-vertical"></i>
                        Height:
                      </span>
                      <span className="detail-value">{device.height ? `${device.height} m` : "Not set"}</span>
                    </div>

                    <div className="detail-item">
                      <span className="detail-label">
                        <i className="fas fa-map-marker-alt"></i>
                        Location:
                      </span>
                      <span className="detail-value">
                        {device.location?.coordinates
                          ? `${device.location.coordinates[1].toFixed(4)}, ${device.location.coordinates[0].toFixed(4)}`
                          : "Not set"}
                      </span>
                    </div>

                    <div className="detail-item parameters-detail">
                      <span className="detail-label">
                        <i className="fas fa-list"></i>
                        Parameters ({device.selectedParameters?.length || 0}):
                      </span>
                      <div className="parameters-list">
                        {device.selectedParameters?.length > 0 ? (
                          device.selectedParameters.map((paramId) => (
                            <span key={paramId} className="parameter-tag">
                              {getParameterName(paramId)}
                              <span className="parameter-unit-small">({getParameterUnit(paramId)})</span>
                            </span>
                          ))
                        ) : (
                          <span className="no-parameters">No parameters selected</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DeviceData
